import { Entity, Column, PrimaryGeneratedColumn, Unique, ManyToOne, JoinColumn, OneToMany, JoinTable, Index } from 'typeorm';

import { UserUtils, FormField } from './user.utils';
import { PublicSite, Site } from '../site/site.entity';
import { UserSettings, UserSettingsPrivate } from './models';
import { Business, BusinessStats, PublicBusiness } from '../business/business.entity';
import { BusinessRole, BusinessUser } from '../business/business-user.entity';
import { AuthenticationStrategy, PublicAuthenticationStrategy } from '../authentication-strategy/authentication-strategy.entity';
import { UserRole } from './models/user-role';

export { UserRole, UserRoleMap } from './models/user-role';

export enum ActivationStatus {
	Pending = 'pending',
	Activated = 'activated'
}

export enum AuthMethod {
	Email = 'email',
	Phone = 'phone'
}

export enum AuthFlow {
	Login = 'login',
	Signup = 'signup'
}

export const UserRoleToString = {
	[UserRole.SuperAdmin]: 'Super Admin',
	[UserRole.Admin]: 'Admin',
	[UserRole.Editor]: 'Editor',
	[UserRole.User]: 'User',
	[UserRole.Guest]: 'Guest'
};

export type UserStats = {
	projectsCount: number;
	businessesCount: number;
};
export type PublicUser = Pick<
	User,
	| 'id'
	| 'email'
	| 'phone'
	| 'siteId'
	| 'role'
	| 'reviewer'
	| 'created'
	| 'lastSeen'
	| 'settings'
	| 'acceptedTerms'
	| 'acceptedTermsDate'
	| 'emailValidated'
	| 'phoneValidated'
> & {
	fullName?: string;
	site?: PublicSite;
	businesses?: PublicBusiness[];
	isApprover?: boolean;
	adEditor?: boolean;
	canPurchase?: boolean;
	businessRole?: BusinessRole;
	authenticationStrategyId: string;
	authenticationStrategy: PublicAuthenticationStrategy;
	reporting?: boolean;
	stats?: UserStats;
};
@Entity('users')
@Index(['emailNormalized', 'site'], { unique: true, where: '"emailNormalized" IS NOT NULL' })
@Index(['phone', 'site'], { unique: true, where: '"phone" IS NOT NULL' })
export class User {
	constructor(value?: Partial<User>) {
		if (value) {
			value = structuredClone(value);
		}
		for (const k in value) {
			this[k] = value[k];
		}
	}

	@PrimaryGeneratedColumn('uuid')
	public id: string;

	@Column('text', { nullable: true })
	public email?: string;

	@Column('text', { nullable: true })
	public emailNormalized?: string;

	@Column('text', { nullable: true })
	public phone?: string;

	//reporting
	@Column('boolean', { nullable: false, default: false })
	public reporting: boolean;

	@Column('text')
	public siteId: string;
	@ManyToOne(
		type => Site,
		site => site.id,
		{
			eager: false,
			onDelete: 'CASCADE'
		}
	)
	@JoinColumn({ name: 'siteId' })
	public site: Site | Partial<Site>;

	@Column({
		type: 'enum',
		enum: UserRole,
		default: UserRole.Guest
	})
	public role: UserRole;

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public created: string;

	@Column({
		type: 'enum',
		enum: ActivationStatus,
		default: ActivationStatus.Pending
	})
	public activationStatus: ActivationStatus;

	@Column({ type: 'boolean', nullable: false, default: false })
	public reviewer: boolean;

	@Column({ type: 'boolean', default: false })
	public deactivated: boolean;

	@Column('text', { nullable: true })
	public singlePass: string;

	@Column('timestamptz', { nullable: true })
	public singlePassExpire: string;

	@Column('text', { array: true, nullable: true })
	public authTokens: string[];

	@Column('text', { array: true, default: [], select: false })
	public oauthDelegatedTokens: string[];

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public lastSeen: string;

	@Column('jsonb', { nullable: true, default: {} })
	public settings: UserSettings;

	@Column('text', {
		name: 'settingsPrivate',
		nullable: true
	})
	// No initializer: a partial `new User({ id, ... })` passed to save() must leave
	// unspecified columns undefined (skipped), not null (written). See authenticationStrategyId.
	private _settingsPrivate: string;
	public get settingsPrivate(): UserSettingsPrivate {
		return UserUtils.decryptProfile(this._settingsPrivate, this.id);
	}
	public set settingsPrivate(value: UserSettingsPrivate) {
		this._settingsPrivate = UserUtils.encryptProfile(value, this.id);
	}

	@Column('timestamptz', { nullable: true })
	public emailValidationExpire: string;

	@Column('boolean', { default: false })
	public emailValidated: boolean;

	@Column('timestamptz', { nullable: true })
	public phoneValidationExpire: string;

	@Column('boolean', { default: false })
	public phoneValidated: boolean;

	@Column('boolean', { default: false })
	public acceptedTerms: boolean;

	@Column('timestamptz', { nullable: true })
	public acceptedTermsDate: string;

	@OneToMany(
		() => BusinessUser,
		businessUser => businessUser.user,
		{
			onDelete: 'CASCADE'
		}
	)
	@JoinTable({ name: 'businessUsers' })
	public userBusinesses?: BusinessUser[] | Partial<BusinessUser[]>;

	public businesses?: Business[];

	// These fields are applied if this user is queried as a business user.
	public isApprover?: boolean;
	public canPurchase?: boolean;
	public adEditor?: boolean;

	public businessRole?: BusinessUser['role'];
	// No initializer. A `= null` default made every partial save (SAML challenge,
	// partner request-token) write NULL here, which broke sign-in for that user.
	@Column('text', { nullable: true })
	authenticationStrategyId: string;
	@ManyToOne(
		type => AuthenticationStrategy,
		authenticationStrategy => authenticationStrategy.id,
		{
			nullable: true,
			onDelete: 'CASCADE'
		}
	)
	@JoinColumn({ name: 'authenticationStrategyId' })
	authenticationStrategy: AuthenticationStrategy | Partial<AuthenticationStrategy>;

	@Column('text', { nullable: true })
	authChallenge?: string;

	@Column({
		name: 'lastUsedAuthMethod',
		type: 'enum',
		enum: AuthMethod,
		nullable: true
	})
	lastUsedAuthMethod?: AuthMethod;

	public toPublic(self: boolean = false, stats?: UserStats, businessStats?: Record<string, BusinessStats>) {
		let pub: Partial<PublicUser> = {
			id: this.id,
			fullName: this.settings?.fullName,
			email: this.email,
			phone: this.phone,
			siteId: this.siteId,
			role: this.role,
			reviewer: this.reviewer,
			isApprover: this.isApprover,
			adEditor: this.adEditor,
			canPurchase: this.canPurchase,
			created: this.created,
			lastSeen: this.lastSeen,
			authenticationStrategyId: this.authenticationStrategyId,
			acceptedTerms: this.acceptedTerms,
			acceptedTermsDate: this.acceptedTermsDate,
			reporting: this.reporting,
			emailValidated: this.emailValidated,
			phoneValidated: this.phoneValidated
		};

		if (this.authenticationStrategy) {
			pub.authenticationStrategy = new AuthenticationStrategy(this.authenticationStrategy).toPublic();
		}

		if (self) {
			pub.settings = this.settings;
		}

		if (this.isApprover) {
			pub.isApprover = this.isApprover;
		}

		if (this.adEditor) {
			pub.adEditor = this.adEditor;
		}

		if (this.reporting) {
			pub.reporting = this.reporting;
		}

		if (this.businessRole) {
			pub.businessRole = this.businessRole;
		}

		if (stats) {
			pub.stats = stats;
		}

		// Only attach per-business stats when they were actually computed —
		// fabricated zeros would render as a misleading "0 Projects · 0 Users"
		// in the switcher until the next /me refresh.
		if (this.userBusinesses?.length && !this.businesses?.length) {
			pub.businesses = this.userBusinesses.map(b => {
				const businessStat = businessStats
					? businessStats[b.businessesId] || { projectsCount: 0, usersCount: 0, foldersCount: 0 }
					: undefined;
				return new BusinessUser(b).toBusiness().toPublic(false, businessStat);
			});
		} else if (this.businesses?.length) {
			pub.businesses = this.businesses.map(b => {
				const businessStat = businessStats
					? businessStats[b.id] || { projectsCount: 0, usersCount: 0, foldersCount: 0 }
					: undefined;
				return new Business(b).toPublic(false, businessStat);
			});
		}

		return pub as PublicUser;
	}
}
